import { useEffect, useMemo } from 'react'
import { FormLabel, Grid, TextField } from '@mui/material'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import InputField from 'components/form/InputField'
import FormSectionContainer from 'components/layout/FormSectionContainer'
import InputErrorMessage from 'components/ui/InputErrorMessage'
import { UseFormReturn } from 'react-hook-form'
import { getChallengeTypeName, StablefordResultAggregationType } from 'utils/challenges'
import { useAppDispatch } from 'store'
import { selectCourses } from 'store/courses/slice'
import { getCoursesById } from 'store/courses/thunks'
import SearchableSelect from '../SearchableSelect'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import styled from 'styled-components'
import { formatDateTime } from 'utils/formatDate'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime, Settings } from 'luxon'
import { ChallengeTypeConfig } from 'api/types'

interface Props {
  form: UseFormReturn<any>
  challenge: Challenge
}

export default function ChallengeReadOnlyPhase1({ form, challenge }: Props): JSX.Element {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { register, setValue, watch } = form

  const { courses, status: coursesStatus } = useSelector(selectCourses)
  const watchedCourseIds: string[] = watch('roundEligibility.courseIds') ?? challenge.roundEligibility?.courseIds ?? []

  const challengeType = watch('typeConfig.type')
  Settings.defaultLocale = 'fi-FI'

  useEffect(() => {
    if (challenge?.schedule?.startAt) {
      setValue('schedule.startAt', formatDateTime(new Date(challenge.schedule.startAt)).slice(0, 16))
    }
  }, [challenge?.schedule?.startAt, setValue])

  useEffect(() => {
    if (challenge?.schedule?.endAt) {
      setValue('schedule.endAt', formatDateTime(new Date(challenge.schedule.endAt)).slice(0, 16))
    }
  }, [challenge?.schedule?.endAt, setValue])

  useEffect(() => {
    if (challenge.roundEligibility?.courseIds) {
      setValue('roundEligibility.courseIds', challenge.roundEligibility.courseIds)
    }
  }, [challenge.roundEligibility?.courseIds, setValue])

  const coursesOptions: SelectOption[] = useMemo(
    () =>
      courses.map((course: Course) => {
        return { key: course.id, label: course.name }
      }),
    [courses],
  )

  const selectedCourses: SelectOption[] = useMemo(() => {
    const ids = watchedCourseIds.length ? watchedCourseIds : challenge.roundEligibility?.courseIds ?? []

    return ids
      .map((id) => coursesOptions.find((option) => option.key.toString() === id))
      .filter((option): option is SelectOption => option !== undefined)
  }, [watchedCourseIds, coursesOptions, challenge.roundEligibility?.courseIds])

  useEffect(() => {
    if (challenge.roundEligibility?.courseIds && challenge.roundEligibility?.courseIds.length > 0) {
      dispatch(getCoursesById({ courseIds: challenge.roundEligibility?.courseIds.join(',') }))
    }
  }, [challenge.roundEligibility?.courseIds])

  const getCountMethod = () => {
    const strokeplay = watch('typeConfig.strokeplay')
    const stableford = watch('typeConfig.stableford')

    if (strokeplay) {
      return strokeplay.useHandicap ? 'net' : 'gross'
    }

    if (stableford) {
      return stableford.resultAggregation === StablefordResultAggregationType.TOTAL ? 'Total' : 'Best'
    }

    return ''
  }

  return (
    <>
      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>{intl.formatMessage({ id: 'challenges.typeAndScheduling' })}</h2>
          </Grid>
          <Grid item xs={4}>
            <InputField
              register={register}
              name="info.name"
              type="text"
              label={intl.formatMessage({ id: 'strings.challengeName' })}
              outlined
              required
              disabled
            />
          </Grid>
          <Grid item xs={8} />
          <Grid item xs={challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER ? 3 : 4}>
            <FormLabel disabled sx={{ marginBottom: '4px', display: 'flex' }}>
              {intl.formatMessage({ id: 'challenges.type' })}
            </FormLabel>
            <TextField
              name="typeConfig.type"
              type="text"
              value={getChallengeTypeName(watch('typeConfig.type'), intl)}
              disabled
              sx={{ width: '100%' }}
            />
          </Grid>

          {(watch('typeConfig.strokeplay') || watch('typeConfig.stableford')) && (
            <Grid item xs={challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER ? 3 : 4}>
              <FormLabel disabled sx={{ marginBottom: '4px', display: 'flex' }}>
                {intl.formatMessage({ id: 'strings.count' })}
              </FormLabel>
              <TextField name="calculationType" value={getCountMethod()} required disabled sx={{ width: '100%' }} />
            </Grid>
          )}

          {!(watch('typeConfig.strokeplay') || watch('typeConfig.stableford')) && (
            <Grid item xs={challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER ? 3 : 4}>
              <FormLabel disabled sx={{ marginBottom: '4px', display: 'flex' }}>
                {intl.formatMessage({ id: 'challenges.target' })}
              </FormLabel>
              <TextField
                name="typeConfig.target"
                type="text"
                disabled
                value={watch('typeConfig.target')}
                sx={{ width: '100%' }}
              />
            </Grid>
          )}
        </Grid>
      </FormSectionContainer>

      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>{intl.formatMessage({ id: 'strings.locationChallengeCanBePerformed' })}</h2>
          </Grid>
          <Grid item xs={4}>
            <InputField
              name="roundEligibility.countriesInclusive"
              label={intl.formatMessage({ id: 'challenges.type' })}
              type="text"
              placeholder="Include"
              outlined
              disabled
            />
          </Grid>
          <Grid item xs={4}>
            <InputField
              register={register}
              name="roundEligibility.minCoursePar"
              type="number"
              label={intl.formatMessage({ id: 'strings.minCoursePar' })}
              outlined
              value={watch('roundEligibility.minCoursePar', challenge?.roundEligibility?.minCoursePar)}
              inputProps={{
                min: 0,
                max: 99,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={4}></Grid>

          <Grid item xs={4}>
            {coursesStatus === 'loading' ? (
              <SpinnerContainer>
                <LoadingSpinnerGreen size={72} />
              </SpinnerContainer>
            ) : (
              <SearchableSelect
                name="roundEligibility.courseIds"
                label={intl.formatMessage({ id: 'strings.courses' })}
                form={form}
                options={coursesOptions}
                status={coursesStatus}
                initialValue={selectedCourses}
                readOnly={true}
                disabled
              />
            )}
          </Grid>
        </Grid>
      </FormSectionContainer>

      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>{intl.formatMessage({ id: 'strings.scheduling' })}</h2>
          </Grid>
          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={'fi-FI'}>
            <Grid item xs={12} sm={4}>
              <FormLabel disabled sx={{ marginBottom: '4px', display: 'flex' }}>
                {intl.formatMessage({ id: 'challenges.startTime' })}
              </FormLabel>
              <DatePicker
                disabled={true}
                value={challenge.schedule?.startAt ? DateTime.fromISO(challenge.schedule.startAt) : null}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    paddingRight: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormLabel disabled sx={{ marginBottom: '4px', display: 'flex' }}>
                {intl.formatMessage({ id: 'challenges.endTime' })}
              </FormLabel>
              <DatePicker
                disabled={true}
                value={challenge.schedule?.endAt ? DateTime.fromISO(challenge.schedule.endAt) : null}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    paddingRight: 2,
                  },
                }}
              />
              {watch('schedule.endAt') && watch('schedule.startAt') > watch('schedule.endAt') && (
                <InputErrorMessage>
                  {intl.formatMessage({ id: 'strings.endTimeShouldBeGreaterThanStartTime' })}
                </InputErrorMessage>
              )}
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <FormLabel disabled sx={{ marginBottom: '4px', display: 'flex' }}>
                {intl.formatMessage({ id: 'strings.timezone' })}
              </FormLabel>
              <TextField name="timeZoneId" value="Helsinki (UTC+02:00)" disabled />
            </Grid>
          </LocalizationProvider>
        </Grid>
      </FormSectionContainer>
    </>
  )
}

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`
